import { useCallback } from 'react'

import { SwapDirection } from '~/components/forms/swap/utils'
import {
  CreateTransactionData,
  EarnDataPayload,
} from '~/hooks/smart-account-actions/createTransactionData.type'
import {
  EarnTransactionDepositParams,
  EarnTransactionWithdrawParams,
} from '~/hooks/smart-account-actions/types'
import { useCreateTransaction } from '~/hooks/smart-account-actions/useCreateTransaction'
import { BasicTransactionType, EarnAction, OrgTransaction } from '~/types'
import { Address } from '~/utils/address'
import { displayBigNumber } from '~/utils/display'
import { bpsToPercentage, getDefaultSlippageBps } from '~/utils/swap'

export const useCreateEarnTransaction = (): {
  createEarnTransaction: (
    data: EarnTransactionDepositParams | EarnTransactionWithdrawParams,
    dontOpenDialog: boolean,
    onSuccess?: (transaction: OrgTransaction) => void,
  ) => Promise<void>
  isProcessing: boolean
} => {
  const { createTransaction, isProcessing } = useCreateTransaction()

  const createEarnTransaction = useCallback(
    async (
      data: EarnTransactionWithdrawParams | EarnTransactionDepositParams,
      dontOpenDialog: boolean,
      onSuccess?: (transaction: OrgTransaction) => void,
    ) => {
      const earnData: EarnDataPayload = {
        chainId: data.chainId,
        shares: data.shares.toString(),
        vault: data.vaultToken.address,
        assets: data.assets.toString(),
        asset: data.assetToken.address,
        action: data.action,
        pauseAutoEarnSeconds:
          data.action === EarnAction.WITHDRAW
            ? data.pauseAutoEarnSeconds
            : undefined,
      }
      const swapData =
        data.action === EarnAction.DEPOSIT &&
        !Address.equal(data.sourceToken.address, data.assetToken.address)
          ? {
              buyTokenAddress: data.assetToken.address,
              buyAmount: data.assets.toString(),
              sellAmount: data.sourceAmount.toString(),
              sellTokenAddress: data.sourceToken.address,
              direction: SwapDirection.SELL,
              provider: data.provider,
              // TODO: implement slippage in earn form
              slippage: bpsToPercentage(
                getDefaultSlippageBps(
                  data.sourceToken.symbol,
                  data.assetToken.symbol,
                ),
              ).toString(),
            }
          : undefined

      const name =
        data.action === EarnAction.DEPOSIT
          ? `Deposit ${displayBigNumber(data.sourceAmount, {
              displayDecimals: 4,
              tokenDecimals: data.sourceToken?.decimals,
              minDisplayDecimals: 0,
            })} ${data.assetToken.symbol} into ${data.assetToken.symbol}`
          : `Withdraw ${displayBigNumber(data.assets, {
              displayDecimals: 4,
              tokenDecimals: data.assetToken.decimals,
              minDisplayDecimals: 0,
            })} ${data.assetToken.symbol} from ${data.assetToken.symbol}`

      const createTransactionData: CreateTransactionData = {
        chainId: data.chainId,
        name,
        smartAccountId: data.smartAccountId,
        smartAccountAddress: data.smartAccountAddress,
        calls: data.calls,
        isOfframp: false,
        onSuccess,
        memo: data.memo,
        types: [
          BasicTransactionType.Earn,
          ...(swapData ? [BasicTransactionType.Swap] : []),
        ],
        transactionData: {
          earnData,
          swapData,
        },
      }

      const loggingData = {
        ...(data.loggingData ? data.loggingData : {}),
      }
      createTransaction({
        transactionId: undefined,
        txData: createTransactionData,
        dontOpenDialog,
        loggingData,
      })
    },
    [createTransaction],
  )

  return {
    createEarnTransaction,
    isProcessing,
  }
}
